import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { Button } from "./ui/button";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  closeLabel?: string;
}

export function Modal({ isOpen, onClose, title, subtitle, children, closeLabel = "Close" }: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-md transition-all duration-300 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className="w-full max-w-xl overflow-hidden rounded-2xl bg-card border border-border/80 shadow-2xl p-6 md:p-8 animate-in fade-in zoom-in-95 duration-200"
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h3 id="modal-title" className="text-2xl font-bold text-brand-primary leading-tight">
              {title}
            </h3>
            {subtitle && <p className="text-sm font-medium text-brand-secondary/80 mt-1">{subtitle}</p>}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={closeLabel}
            className="h-8 w-8 rounded-full border border-border/40 hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="text-foreground/90 space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
          {children}
        </div>
      </div>
    </div>
  );
}
export default Modal;
